import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import { ScreenContext } from 'components/Providers'

import { useStaggeredMovement } from 'utils/hooks'

import colors from 'styles/colors'
import text from 'styles/text'
import media from 'styles/media'

import Footer from 'components/Footer'
import EmailCapture from 'components/forms/EmailCapture'

import BackgroundWEBP from 'assets/images/background.webp'
import BitMaskPNG from 'assets/images/bitMaskBlack.png'
import DiamondMP4 from 'assets/videos/diamond_H.265.mp4'
import DiamondWEBM from 'assets/videos/diamond_VP9.webm'
import DiamondWEBP from 'assets/images/diamond.webp'

const CTA: React.FC = () => {

  const screen = useContext(ScreenContext)

  const [diamond1Ref, setDiamond1Ref] = useState<HTMLVideoElement | HTMLImageElement | null>(null)
  const [diamond2Ref, setDiamond2Ref] = useState<HTMLVideoElement | HTMLImageElement | null>(null)
  const [diamond3Ref, setDiamond3Ref] = useState<HTMLVideoElement | HTMLImageElement | null>(null)

  useStaggeredMovement([
    diamond1Ref,
    diamond2Ref,
    diamond3Ref
  ])

  return (
    <Wrapper >
      <BG src={BackgroundWEBP} alt="backgruond" loading="lazy"/>
      <BitMask src={BitMaskPNG} alt="pixels" loading="lazy"/>

        {(screen.desktop || screen.fullWidth) && 
          <>
            <Diamond1Video ref={ref => setDiamond1Ref(ref)} autoPlay loop muted controls={false} playsInline poster={DiamondWEBP} preload="none">
              <source src={DiamondMP4} type="video/mp4; codecs=hvc1"/>
              <source src={DiamondWEBM} type="video/webm"/>
            </Diamond1Video>

            <Diamond2Video ref={ref => setDiamond2Ref(ref)} autoPlay loop muted controls={false} playsInline poster={DiamondWEBP} preload="none">
              <source src={DiamondMP4} type="video/mp4; codecs=hvc1"/>
              <source src={DiamondWEBM} type="video/webm"/>
            </Diamond2Video>

            <Diamond3Video ref={ref => setDiamond3Ref(ref)} autoPlay loop muted controls={false} playsInline poster={DiamondWEBP} preload="none">
              <source src={DiamondMP4} type="video/mp4; codecs=hvc1"/>
              <source src={DiamondWEBM} type="video/webm"/>
            </Diamond3Video>
          </>
        }

        {(screen.mobile || screen.tablet) &&
          <>
            <Diamond1Image ref={ref => setDiamond1Ref(ref)} src={DiamondWEBP} alt="diamond" loading="lazy"/>
            <Diamond2Image ref={ref => setDiamond2Ref(ref)} src={DiamondWEBP} alt="diamond" loading="lazy"/>
            <Diamond3Image ref={ref => setDiamond3Ref(ref)} src={DiamondWEBP} alt="diamond" loading="lazy"/>
          </>        
        }

      <Content>
        <Top>
          <Title>Never miss an update</Title>
          <Text>
            Stay fresh with the latest info on $FCD and FreshCut.
          </Text>
          <EmailCapture/>
        </Top>
        <Footer/>
      </Content>
    </Wrapper>
  )
}

export default CTA

const Wrapper = styled.section` 
  position: relative;
  width: 100%;

  height: 63.68vw;

  ${media.tablet} {
    width: 100vw;
    height: 89.551vw;
    left: 0vw;
    top: 0vw;
  }

  ${media.mobile} {
    height: 226.667vw;
  }
`

const BG = styled.img`
  position: absolute;
  z-index: 0;
  transform: rotate(180deg);
  object-fit: cover;

  width: 100%;
  height: 100%;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const Top = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  padding-left: 4.17vw;
  padding-top: 6.94vw;

  ${media.mobile} {
    padding-top: 42.67vw;
  }
`

const Title = styled.h6`
  ${text.desktop.h4}
  color: ${colors.pureWhite};
  text-transform: uppercase;

  width: 37vw;
  margin-bottom: 1.39vw;

  ${media.tablet} {
    ${text.tablet.h4} 
    width: 52.391vw;
    margin-bottom: 1.953vw;
  }

  ${media.mobile} {
    ${text.mobile.h5}
    width: 88vw;
    margin-bottom: 5.33vw;
  }
`

const Text = styled.p`
  ${text.desktop.bodyL}
  background: ${colors.horzGradient};
  ${text.style.gradient}

  width: 35.83vw;
  margin-bottom: 4.17vw;

  ${media.tablet} {
    ${text.tablet.bodyL}
    width: 50.391vw;
    height: 8.203vw;
  }

  ${media.mobile} {
    ${text.mobile.bodyR}
    width: 92vw;
    height: 14.933vw;
    margin-bottom: 10.67vw;
  }
`

const BitMask = styled.img`
  position: absolute;
  top: 0;
  right: 0;

  width: 31.67vw;
  height: 39.59vw;

  ${media.tablet} {
    width: 44.531vw;
    height: 55.664vw;
    left: 55.469vw;
    top: 0vw;
  }

  ${media.mobile} {
    width: 60.8vw;
    height: 76vw;
  }
`

const Video = styled.video`
  position: absolute;
`

const Image = styled.img`
  position: absolute;
  mix-blend-mode: screen;
`

const Diamond1 = `  
  height: 27.78vw;
  right: 10.28vw;
  top: -9.17vw;
  transform: rotate(-15deg);

  ${media.tablet} {
    height: 39.063vw;
    left: 56.121vw;
    top: -11.136vw;
  }

  ${media.mobile} {
    width: 78.667vw;
    height: auto;
    left: 1.867vw;
    top: -7.909vw;
  }
`

const Diamond2 = `
  height: 13.89vw;
  top: 9.93vw;
  right: 2.33vw;
  transform: rotate(15deg);

  ${media.tablet} {
    height: 19.531vw;
    left: 57.398vw;
    top: 24.996vw;
  }

  ${media.mobile} {
    width: 28.667vw;
    height: auto;
    left: 70vw;
    top: 19vw;
  }
`

const Diamond3 = `
  height: 6vw;
  top: 30.5vw;
  right: 22.75vw;
  transform: rotate(220deg);

  ${media.tablet} {
    height: 9.766vw;
    left: 84.695vw;
    top: 36.324vw;
  }

  ${media.mobile} {
    width: 18vw;
    height: auto;
    left: 19vw;
    top: 137.933vw;
    transform: rotate(330deg)
  }
`

const Diamond1Video = styled(Video)`
  ${Diamond1}
`

const Diamond1Image = styled(Image)`
  ${Diamond1}
`

const Diamond2Video = styled(Video)`
  ${Diamond2}
`

const Diamond2Image = styled(Image)`
  ${Diamond2}
`

const Diamond3Video = styled(Video)`
  ${Diamond3}
`

const Diamond3Image = styled(Image)`
  ${Diamond3}
`